import { useAtomValue } from 'jotai/utils';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { useFeatureAvailability } from 'src/hooks/useFeatureAvailability';

/**
 * Whether the (built-in) Scheduled agents feature should render its
 * read-only "Not enabled" state.
 *
 * Scheduled agents is always shown and reachable from the sidebar, but the
 * page falls back to `FeatureUnavailable` when:
 *  - the account doesn't have the AI addon enabled, or
 *  - the runtime probe reports the feature unavailable, or
 *  - the (optionally passed) request error signals the feature is off or the
 *    route is missing on the old release during the rollout.
 *
 * Pass `error` only for list-style queries where a 404 means the whole
 * feature route is missing. Detail/replay pages omit it so a 404 on a
 * specific schedule id keeps its own "not found" handling.
 */
export function useAiSchedulesUnavailable(error?: unknown): boolean {
  const featureStatus = useFeatureAvailability('ai-schedules');
  const addonsSettings = useAtomValue(addonsSettingsAtom);
  const aiAddonEnabled = Boolean(
    addonsSettings?.AI_ASSISTANT?.added && addonsSettings?.AI_ASSISTANT?.enabled,
  );
  const err = error as { error?: string; status?: number } | undefined;

  return (
    !aiAddonEnabled ||
    featureStatus === 'unavailable' ||
    err?.error === 'scheduling_disabled' ||
    err?.status === 503 ||
    err?.status === 404
  );
}
